import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { getCourses } from "@/lib/api";
import { PILARES, TESTIMONIOS, COMU_FEATS } from "@/data";
import NeuralCanvas from "@/components/NeuralCanvas";
import Robot3D from "@/components/Robot3D";
import ChatDemo from "@/components/ChatDemo";
import FormContacto from "@/components/FormContacto";
import { Countdown, PlazasVIP } from "@/components/Offer";
import { useAuth } from "@/context/AuthContext";
import { useTheme } from "@/context/ThemeContext";
import { MessageCircle, Sun, Moon } from "lucide-react";
import { fileUrl, thumbUrl } from "@/lib/api";

const WA_NUMERO = "34633636438";
const WA_LINK = `https://wa.me/${WA_NUMERO}?text=${encodeURIComponent("¡Hola! Quiero información sobre el Máster de Liberti Club IA.")}`;

const TOOLS = ["ChatGPT", "Claude", "Gemini", "Midjourney", "Runway", "ElevenLabs", "n8n", "Zapier"];

function LogoTxt({ size = 15 }) {
  return (
    <div className="logo-txt" style={{ fontSize: size }}>
      LIBERTI CLUB <em>IA</em>
    </div>
  );
}

function TemarioMaster() {
  const { data: cursos = [] } = useQuery({ queryKey: ["courses"], queryFn: getCourses });
  const master = cursos[0];
  const modulos = master?.modulos || [];
  return (
    <div className="temario" data-testid="temario-master" style={{ maxWidth: 860, margin: "0 auto", display: "flex", flexDirection: "column", gap: 14 }}>
      {modulos.map((m, i) => (
        <div key={m.orden || i} className="temario-row" data-testid={`temario-modulo-${i}`}>
          <div style={{ display: "flex", alignItems: "baseline", gap: 16 }}>
            <span className="serif temario-num">{String(i + 1).padStart(2, "0")}</span>
            <div>
              <h3 className="serif temario-title">{m.titulo}</h3>
              {m.descripcion && <p className="temario-desc">{m.descripcion}</p>}
            </div>
          </div>
          {(m.lecciones || []).length > 0 && (
            <div style={{ display: "flex", flexWrap: "wrap", gap: 8, marginTop: 16, paddingLeft: 68 }}>
              {m.lecciones.map((l, li) => (
                <span key={li} className="lec-chip">{l.titulo}</span>
              ))}
            </div>
          )}
        </div>
      ))}
      {modulos.length === 0 && <p style={{ color: "var(--muted)", textAlign: "center" }}>El temario se publicará muy pronto.</p>}
    </div>
  );
}

const IAS_PACK = [
  ["ChatGPT Pro", 200, "openai.com"], ["Claude Max", 200, "claude.ai"], ["Grok Heavy", 200, "x.ai"], ["Perplexity Max", 200, "perplexity.ai"],
  ["TurboScribe", 20, "turboscribe.ai"], ["Artlist", 69, "artlist.io"], ["Gemini Ultra + Veo3", 300, "gemini.google.com"], ["Midjourney Pro", 60, "midjourney.com"],
  ["Magnific", 37, "magnific.ai"], ["Envato Plus", 39, "envato.com"], ["CapCut Pro", 10, "capcut.com"], ["Leonardo AI", 60, "leonardo.ai"],
  ["Canva Pro", 10, "canva.com"], ["Gamma Ultra", 100, "gamma.app"], ["ElevenLabs", 99, "elevenlabs.io"], ["Fish Audio", 100, "fish.audio"],
  ["Algrow", 80, "algrow.ai"], ["AI33 Pro", 40, "ai33.pro"], ["MiniMax", 80, "minimax.io"],
];

function PackIAs({ onComprar }) {
  const total = IAS_PACK.reduce((a, [, p]) => a + p, 0);
  const [shown, setShown] = useState(0);
  const saveRef = React.useRef(null);
  useEffect(() => {
    const el = saveRef.current;
    if (!el) return;
    const io = new IntersectionObserver((entries) => {
      if (entries[0].isIntersecting) {
        io.disconnect();
        const start = performance.now(), dur = 1400;
        const tick = (t) => {
          const p = Math.min(1, (t - start) / dur);
          const eased = 1 - Math.pow(1 - p, 3);
          setShown(Math.round(total * eased));
          if (p < 1) requestAnimationFrame(tick);
        };
        requestAnimationFrame(tick);
      }
    }, { threshold: 0.4 });
    io.observe(el);
    return () => io.disconnect();
  }, [total]);
  return (
    <section className="section" id="pack-ias">
      <div className="sec-head" style={{ textAlign: "center", margin: "0 auto clamp(30px,5vw,54px)" }}>
        <span className="sec-tag" style={{ background: "rgba(255,194,75,.12)", borderColor: "rgba(255,194,75,.4)", color: "#FFC24B" }}>Incluido con tu acceso</span>
        <h2 className="h2">Pack de IAs <strong>gratis 3 meses.</strong></h2>
        <p className="sec-sub">Todas nuestras IAs premium <b>gratis durante 3 meses</b> para todos nuestros alumnos. Deja de pagar suscripciones sueltas.</p>
      </div>
      <div className="pack-grid">
        {IAS_PACK.map(([name, price, domain]) => (
          <div className="pack-card" key={name} data-testid={`pack-ia-${name}`}>
            <img className="pack-logo" src={`https://www.google.com/s2/favicons?domain=${domain}&sz=64`} alt="" loading="lazy"
              onError={(e) => { e.currentTarget.style.display = "none"; e.currentTarget.nextSibling.style.display = "inline-block"; }} />
            <span className="pack-dot" style={{ display: "none" }} />
            <span className="pack-name">{name}</span>
            <span className="pack-price">${price}</span>
          </div>
        ))}
      </div>
      <div className="pack-save" ref={saveRef} data-testid="pack-save">
        <span>Te ahorras <b data-testid="pack-save-amount">+{shown.toLocaleString("es-ES")} $/mes</b></span>
        <span className="pack-save-tag">GRATIS 3 MESES</span>
      </div>
      <div style={{ textAlign: "center", marginTop: 30 }}>
        <button className="btn btn-gold" data-testid="pack-cta" onClick={onComprar}>Quiero el pack + el Máster →</button>
      </div>
    </section>
  );
}

function CursosLanding({ onComprar }) {
  const { data: cursos = [] } = useQuery({ queryKey: ["courses"], queryFn: getCourses });
  if (!cursos.length) return null;
  return (
    <div className="cursos-grid" data-testid="cursos-landing">
      {cursos.map((c) => (
        <button key={c.slug} className="curso-land-card" data-testid={`curso-land-${c.slug}`} onClick={onComprar}>
          <div className="curso-land-cover" style={c.portada ? { backgroundImage: `url(${thumbUrl(c.portada, 800)})` } : {}}>
            {!c.portada && <span className="curso-land-ph">✦</span>}
            {c.destacado && <span className="curso-land-badge">NUEVO</span>}
          </div>
          <div className="curso-land-name">{c.titulo}</div>
        </button>
      ))}
    </div>
  );
}

export default function Landing() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { light, toggle } = useTheme();
  const [showCta, setShowCta] = useState(false);
  useEffect(() => {
    const onScroll = () => setShowCta(window.scrollY > 700);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);
  const onLogin = () => navigate(user ? "/campus" : "/login");
  const onComprar = () => navigate("/checkout?tipo=vip");
  const onAgendar = () => navigate("/agendar");

  return (
    <div className={light ? "lci light" : "lci"}>
      <div className="topbar">
        <header className="nav">
          <div className="logo" onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}>
            <div className="logo-mark">L</div>
            <LogoTxt />
          </div>
          <nav className="nav-links">
            <a href="#master">El Máster</a>
            <a href="#comunidad">Comunidad</a>
            <a href="#precio">Precio</a>
            <a href="#alumnos">Alumnos</a>
            <a href="#contacto">Contacto</a>
            <button className="theme-toggle" data-testid="theme-toggle" onClick={toggle}
              aria-label={light ? "Activar modo oscuro" : "Activar modo claro"} title={light ? "Modo oscuro" : "Modo claro"}>
              {light ? <Moon size={17} /> : <Sun size={17} />}
            </button>
            <button className="btn btn-gold btn-sm nav-cta" data-testid="nav-acceder" onClick={onLogin}>Acceder</button>
          </nav>
        </header>
      </div>

      <section className="hero" style={{ paddingTop: 200 }}>
        <NeuralCanvas />
        <div className="hero-grid">
          <div className="hero-inner">
            <span className="eyebrow rise"><span className="dot" />Admisiones abiertas · España</span>
            <h1 className="h1 rise d1">
              El <strong>Máster</strong> definitivo de Inteligencia Artificial en español.<br />
              Aprende. Aplica. <span className="gold-it">Factura.</span>
            </h1>
            <p className="hero-sub rise d2">
              Un único máster con todo lo que existe en IA — herramientas, automatización, agentes, creación de contenido —
              y el sistema para convertirlo en <b>ingresos reales</b>. Sin humo. Con método, casos reales y comunidad.
            </p>
            <div className="hero-ctas rise d3">
              <button className="btn btn-gold" data-testid="hero-empezar" onClick={onComprar}>Quiero el Máster →</button>
              <a className="btn btn-ghost" href="#master">Ver el temario</a>
            </div>
          </div>
          <Robot3D />
        </div>
      </section>

      <div className="tools-strip" data-testid="tools-strip">
        <span className="tools-label">Dominarás las herramientas líderes del mercado</span>
        <div className="tools-row">
          {TOOLS.map((t) => (
            <span className="tool-logo" key={t} data-testid={`tool-${t}`}>{t}</span>
          ))}
        </div>
      </div>

      <section className="section">
        <div className="sec-head">
          <span className="sec-tag">El método Liberti</span>
          <h2 className="h2">No enseñamos IA. Enseñamos a <strong>vivir de la IA.</strong></h2>
          <p className="sec-sub">Cuatro pilares que separan a quien "sabe de IA" de quien genera ingresos con ella.</p>
        </div>
        <div className="pilares">
          {PILARES.map(([i, t, p]) => (
            <div className="pilar" key={t}>
              <div className="ico">{i}</div><h3>{t}</h3><p>{p}</p>
            </div>
          ))}
        </div>
      </section>

      <section className="section" id="master" style={{ background: "var(--ink2)" }}>
        <div className="sec-head">
          <span className="sec-tag">El Máster</span>
          <h2 className="h2">Un solo máster para <strong>dominarlo todo.</strong></h2>
          <p className="sec-sub">De tu primera conversación con una IA a tu primer negocio automatizado. Acceso de por vida y todas las actualizaciones incluidas.</p>
        </div>
        <TemarioMaster />
        <div style={{ textAlign: "center", marginTop: 40 }}>
          <button className="btn btn-gold" data-testid="master-cta" onClick={onComprar}>Acceder al Máster completo →</button>
        </div>
      </section>

      <section className="section" id="cursos">
        <div className="sec-head">
          <span className="sec-tag">Catálogo</span>
          <h2 className="h2">Todos los <strong>cursos incluidos.</strong></h2>
          <p className="sec-sub">El Máster y cada nuevo curso que publicamos entran automáticamente aquí. Acceso completo con tu membresía.</p>
        </div>
        <CursosLanding onComprar={onComprar} />
      </section>

      <PackIAs onComprar={onComprar} />

      <section className="section" id="comunidad">
        <div className="comu-wrap">
          <div>
            <span className="sec-tag">Comunidad · En vivo</span>
            <h2 className="h2">Nunca aprendes <strong>solo.</strong></h2>
            <p className="sec-sub">
              El campus es un organismo vivo: chat en directo las 24 horas, llamadas semanales con mentores
              y una red de alumnos compartiendo clientes y oportunidades.
            </p>
            <div className="comu-feats">
              {COMU_FEATS.map(([fi, h, p]) => (
                <div className="comu-feat" key={h}>
                  <span className="fi">{fi}</span>
                  <div><h4>{h}</h4><p>{p}</p></div>
                </div>
              ))}
            </div>
          </div>
          <ChatDemo />
        </div>
      </section>

      <section className="section" id="precio">
        <div className="vip-wrap">
          <div>
            <span className="sec-tag">Máster completo · Pago único</span>
            <h2 className="h2">Una sola vez. <span className="gold-it">Para toda la vida.</span></h2>
            <p className="sec-sub">
              Un único pago de <b style={{ color: "var(--cream)" }}>2.900 €</b> te da acceso <b style={{ color: "var(--cream)" }}>de por vida</b> al
              Máster completo, todas las actualizaciones, las mentorías semanales y la comunidad privada de España.
              Sin cuotas, sin sorpresas.
            </p>
          </div>
          <div className="vip-card">
            <div className="vip-inner">
              <span className="badge-oferta">★ Acceso vitalicio</span>
              <h3 style={{ marginTop: 18 }}>MÁSTER IA · COMPLETO</h3>
              <div className="vip-price">
                2.900 €<small> pago único · acceso de por vida</small>
              </div>
              <div className="price-note">Un solo pago. Sin cuotas mensuales.</div>
              <PlazasVIP />
              <Countdown />
              <ul className="vip-list">
                <li>Acceso vitalicio al Máster completo y a todo lo que lancemos</li>
                <li>Mentoría grupal en directo cada semana, para siempre</li>
                <li>Comunidad privada VIP con networking en España</li>
                <li>Revisión personalizada de tu proyecto o negocio</li>
                <li>Plantillas, prompts y sistemas listos para vender</li>
                <li>Certificación oficial Liberti Club IA</li>
              </ul>
              <button className="btn btn-gold" data-testid="vip-reservar" onClick={onComprar} style={{ width: "100%", justifyContent: "center" }}>Acceder al Máster →</button>
              <div className="vip-alt" style={{ marginTop: 12 }}>¿Dudas? <button className="link-gold" onClick={onAgendar}>Agenda una llamada gratuita de 15 min</button></div>
            </div>
          </div>
        </div>
      </section>

      <section className="section" id="alumnos" style={{ background: "var(--ink2)" }}>
        <div className="sec-head">
          <span className="sec-tag">Resultados</span>
          <h2 className="h2">Alumnos que ya <strong>viven de esto.</strong></h2>
        </div>
        <div className="testi-grid">
          {TESTIMONIOS.map((t) => (
            <div className="testi" key={t.who}>
              <div className="testi-stars" data-testid={`testi-stars-${t.who}`} aria-label={`${t.stars || 5} de 5 estrellas`}>
                {"★★★★★".slice(0, t.stars || 5)}
              </div>
              <p>"{t.txt}"</p>
              <div className="who"><b>{t.who}</b>{t.rol}</div>
            </div>
          ))}
        </div>
        <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "Course",
          "name": "Máster en Inteligencia Artificial Aplicada",
          "description": "Máster de Inteligencia Artificial aplicada en español: herramientas, automatización, agentes y monetización.",
          "provider": { "@type": "Organization", "name": "Liberti Club IA", "sameAs": "https://liberticlubia.com" },
          "aggregateRating": { "@type": "AggregateRating", "ratingValue": "5", "reviewCount": String(TESTIMONIOS.length), "bestRating": "5" },
          "review": TESTIMONIOS.map((t) => ({
            "@type": "Review",
            "reviewRating": { "@type": "Rating", "ratingValue": String(t.stars || 5), "bestRating": "5" },
            "author": { "@type": "Person", "name": t.who },
            "reviewBody": t.txt,
          })),
        }) }} />
      </section>

      <section className="section cta-final">
        <span className="sec-tag">Tu momento es ahora</span>
        <h2 className="h2" style={{ maxWidth: 760, marginLeft: "auto", marginRight: "auto" }}>
          La IA no va a quitarte el trabajo.<br />
          <span className="gold-it">Lo hará alguien que la domine.</span>
        </h2>
        <p className="sec-sub" style={{ margin: "20px auto 36px" }}>Máster completo por un único pago de 2.900 € · acceso de por vida.</p>
        <button className="btn btn-gold" data-testid="cta-final-vip" onClick={onComprar}>Acceder al Máster →</button>
      </section>

      <section className="section" id="contacto" style={{ background: "var(--ink2)" }}>
        <div className="contact-wrap">
          <div>
            <span className="sec-tag">Contacto</span>
            <h2 className="h2">¿Dudas? <strong>Hablemos.</strong></h2>
            <p className="sec-sub">Te respondemos en menos de 24 horas. Si prefieres, escríbenos directamente por WhatsApp.</p>
            <div className="contact-info">
              <div className="cinfo"><span className="ci-ico">✉</span><div><b>Email</b><span>hola@liberticlubia.com</span></div></div>
              <div className="cinfo"><span className="ci-ico">◉</span><div><b>WhatsApp</b><a href={WA_LINK} target="_blank" rel="noopener noreferrer" data-testid="whatsapp-contacto" style={{ color: "var(--cream)", textDecoration: "none", fontSize: "14.5px", fontWeight: 300 }}>+34 633 63 64 38 · atención directa</a></div></div>
              <div className="cinfo"><span className="ci-ico">◈</span><div><b>Horario</b><span>Lunes a viernes · 9:00–19:00 (Madrid)</span></div></div>
            </div>
            <button className="btn btn-ghost" data-testid="contacto-agendar" style={{ marginTop: 28 }} onClick={onAgendar}>📅 Agendar llamada gratuita</button>
          </div>
          <FormContacto />
        </div>
      </section>

      <button className={`cta-float ${showCta ? "show" : ""}`} data-testid="cta-flotante" onClick={onComprar}
        aria-label="Quiero el Máster">
        Quiero el Máster →
      </button>

      <a href={WA_LINK} target="_blank" rel="noopener noreferrer" data-testid="whatsapp-float"
        aria-label="Escríbenos por WhatsApp"
        style={{ position: "fixed", right: 22, bottom: 22, zIndex: 90, width: 58, height: 58, borderRadius: "50%",
          background: "#25D366", display: "grid", placeItems: "center", boxShadow: "0 10px 30px rgba(37,211,102,.45)",
          transition: "transform .2s" }}
        onMouseEnter={(e) => (e.currentTarget.style.transform = "scale(1.08)")}
        onMouseLeave={(e) => (e.currentTarget.style.transform = "scale(1)")}>
        <MessageCircle size={28} color="#fff" strokeWidth={2.2} />
      </a>

      <footer className="footer">
        <span>© 2026 LIBERTI CLUB IA · España</span>
        <span style={{ display: "flex", gap: 16, flexWrap: "wrap" }}>
          <button className="link-gold" style={{ textDecoration: "none" }} onClick={() => navigate("/legal/aviso")}>Aviso legal</button>
          <button className="link-gold" style={{ textDecoration: "none" }} onClick={() => navigate("/legal/privacidad")}>Privacidad</button>
          <button className="link-gold" style={{ textDecoration: "none" }} onClick={() => navigate("/legal/cookies")}>Cookies</button>
          <button className="link-gold" style={{ textDecoration: "none" }} onClick={() => navigate("/legal/condiciones")}>Condiciones</button>
        </span>
      </footer>
    </div>
  );
}
